import React, { useState } from 'react';
import {
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    CardContent,
    CardActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useNotify, useLogin, useSafeSetState } from 'react-admin';
// Assuming your authProvider will have a registerUserAndProfile method
// import { useAuthProvider } from 'ra-core';
import { useNavigate, Link } from 'react-router-dom';
import type { SignUpData, Sale, RAFile } from '../types'; // Assuming RAFile is your type for file uploads
import { authProvider as supabaseAuthProviderInstance } from '../providers/supabase'; // Import the actual authProvider
import { TermsAndConditionsCheckbox } from './TermsAndConditionsCheckbox';
import {
    AuthPageLayout,
    AuthButton,
    OrDivider,
    LogoHeader,
} from './components';

// Define lists for Autocomplete options
const ALL_AVAILABLE_STATES = [
    'Montana',
    'Idaho',
    'Washington',
    'Oregon',
    'Wyoming' /* Add more states as needed */,
];
const ALL_AVAILABLE_CARRIERS = [
    'RCIS',
    'NAU',
    'Rain & Hail',
    'AgriSompo',
    'Great American',
    'Pro Ag' /* Add more carriers */,
];

// Combine SignUpData with optional Sale fields for the form
interface RegistrationFormData extends SignUpData {
    confirm_password?: string;
    accept_terms?: boolean;
    // Optional User Profile fields from Sale type
    company_name?: string;
    agent_number?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    avatar?: RAFile | string; // Can be file for upload or string URL if already exists (though unlikely on register)
    company_logo?: RAFile | string;
    // Optional Agent Details fields
    licensed_states?: string[]; // Changed from _input
    appointed_carriers?: string[]; // Changed from _input
}

export const RegistrationPage = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const authProvider = supabaseAuthProviderInstance; // Use the imported instance
    const [loading, setLoading] = useSafeSetState(false);
    const [registrationComplete, setRegistrationComplete] =
        useSafeSetState(false); // New state

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<RegistrationFormData>({
        mode: 'onChange', // or 'onBlur'
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirm_password: '',
            accept_terms: false,
            company_name: '',
            agent_number: '',
            street: '',
            city: '',
            state: '',
            zip: '',
            licensed_states: [], // Changed
            appointed_carriers: [], // Changed
        },
    });

    const password = watch('password');
    const licensedStatesValue = watch('licensed_states');
    const appointedCarriersValue = watch('appointed_carriers');

    const onSubmit: SubmitHandler<RegistrationFormData> = async data => {
        setLoading(true);
        const {
            confirm_password,
            accept_terms,
            avatar, // Destructure avatar
            company_logo, // Destructure company_logo
            ...registrationAndProfileData // This will contain all fields for signUp and sales profile
        } = data;

        // Prepare data for authProvider, ensuring avatar/logo are not RAFile objects for now
        const dataForAuthProvider: Partial<Sale> & SignUpData = {
            ...(registrationAndProfileData as Omit<
                typeof registrationAndProfileData,
                'avatar' | 'company_logo'
            >),
            // If avatar is a string (e.g., URL), pass it, otherwise undefined (or handle RAFile later)
            avatar: typeof avatar === 'string' ? avatar : undefined,
            company_logo:
                typeof company_logo === 'string' ? company_logo : undefined,
            // Ensure all required SignUpData fields are present if not already in registrationAndProfileData
            email: registrationAndProfileData.email!, // Asserting non-null as it's required
            password: registrationAndProfileData.password!, // Asserting non-null
            first_name: registrationAndProfileData.first_name!,
            last_name: registrationAndProfileData.last_name!,
        };

        try {
            if (!authProvider.registerUserAndProfile) {
                throw new Error(
                    'registerUserAndProfile method not implemented in authProvider'
                );
            }
            await authProvider.registerUserAndProfile(dataForAuthProvider);

            notify(
                'Registration successful! Your account is pending approval.',
                { type: 'success' }
            );
            setRegistrationComplete(true); // Set state to show message
            setLoading(false); // Stop loading indicator
        } catch (error: any) {
            setLoading(false);
            notify(
                typeof error === 'string'
                    ? error
                    : typeof error === 'undefined' || !error.message
                      ? 'ra.auth.sign_up_error'
                      : error.message,
                {
                    type: 'error',
                    messageArgs: {
                        _:
                            typeof error === 'string'
                                ? error
                                : error && error.message
                                  ? error.message
                                  : undefined,
                    },
                }
            );
        }
    };

    if (registrationComplete) {
        return (
            <AuthPageLayout centered maxWidth={500}>
                <LogoHeader />
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Typography variant="h6" component="h1" gutterBottom>
                        Registration Submitted!
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ mb: 3, color: 'text.secondary' }}
                    >
                        Thanks for registering! Your account is currently
                        pending approval. You will receive an email once your
                        account has been reviewed and approved by an
                        administrator.
                    </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/login')}
                        fullWidth
                        sx={{ mx: 2 }}
                    >
                        Go to Login Page
                    </Button>
                </CardActions>
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout>
            <LogoHeader />

            <CardContent sx={{ px: 2, py: 2, maxWidth: 400, mx: 'auto' }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography variant="h6" gutterBottom>
                        New Account Form
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        Please complete the form below. Once submitted, your
                        account will be reviewed for approval.
                    </Typography>
                    <TextField
                        {...register('first_name', {
                            required: 'First name is required',
                        })}
                        label="First Name"
                        fullWidth
                        margin="normal"
                        error={!!errors.first_name}
                        helperText={errors.first_name?.message}
                        disabled={loading}
                    />
                    <TextField
                        {...register('last_name', {
                            required: 'Last name is required',
                        })}
                        label="Last Name"
                        fullWidth
                        margin="normal"
                        error={!!errors.last_name}
                        helperText={errors.last_name?.message}
                        disabled={loading}
                    />
                    <TextField
                        {...register('email', {
                            required: 'Email is required',
                            pattern: {
                                value: /\S+@\S+\.\S+/,
                                message: 'Invalid email address',
                            },
                        })}
                        label="Email"
                        type="email"
                        fullWidth
                        margin="normal"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={loading}
                    />
                    <Controller
                        name="password"
                        control={control}
                        rules={{ required: 'Password is required' }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                type="password"
                                label="Password"
                                fullWidth
                                margin="normal"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                disabled={loading}
                            />
                        )}
                    />
                    <Controller
                        name="confirm_password"
                        control={control}
                        rules={{
                            required: 'Confirm password is required',
                            validate: value =>
                                value === password || 'Passwords do not match',
                        }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                type="password"
                                label="Confirm Password"
                                fullWidth
                                margin="normal"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                disabled={loading}
                            />
                        )}
                    />

                    <Accordion
                        sx={{
                            mt: 2,
                            mb: 1,
                            boxShadow: 'none',
                            '&.Mui-expanded': { margin: '16px 0 8px 0' },
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">
                                Agent Details (Optional)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                States Licensed In:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                {ALL_AVAILABLE_STATES.map(state => {
                                    const isSelected =
                                        licensedStatesValue?.includes(state);
                                    return (
                                        <Chip
                                            key={state}
                                            icon={
                                                isSelected ? (
                                                    <CheckIcon
                                                        sx={{
                                                            zIndex: 1,
                                                            pointerEvents:
                                                                'none',
                                                        }}
                                                    />
                                                ) : undefined
                                            }
                                            label={state}
                                            clickable
                                            color={
                                                isSelected
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            variant={
                                                isSelected
                                                    ? 'filled'
                                                    : 'outlined'
                                            }
                                            onClick={() => {
                                                const currentSelectedStates =
                                                    licensedStatesValue || [];
                                                const newSelectedStates =
                                                    isSelected
                                                        ? currentSelectedStates.filter(
                                                              s => s !== state
                                                          )
                                                        : [
                                                              ...currentSelectedStates,
                                                              state,
                                                          ];
                                                setValue(
                                                    'licensed_states',
                                                    newSelectedStates,
                                                    {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    }
                                                );
                                            }}
                                            disabled={loading}
                                        />
                                    );
                                })}
                            </Box>

                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Carriers Appointed With:
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                }}
                            >
                                {ALL_AVAILABLE_CARRIERS.map(carrier => {
                                    const isSelected =
                                        appointedCarriersValue?.includes(
                                            carrier
                                        );
                                    return (
                                        <Chip
                                            key={carrier}
                                            icon={
                                                isSelected ? (
                                                    <CheckIcon
                                                        sx={{
                                                            zIndex: 1,
                                                            pointerEvents:
                                                                'none',
                                                        }}
                                                    />
                                                ) : undefined
                                            }
                                            label={carrier}
                                            clickable
                                            color={
                                                isSelected
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            variant={
                                                isSelected
                                                    ? 'filled'
                                                    : 'outlined'
                                            }
                                            onClick={() => {
                                                const currentSelectedCarriers =
                                                    appointedCarriersValue ||
                                                    [];
                                                const newSelectedCarriers =
                                                    isSelected
                                                        ? currentSelectedCarriers.filter(
                                                              c => c !== carrier
                                                          )
                                                        : [
                                                              ...currentSelectedCarriers,
                                                              carrier,
                                                          ];
                                                setValue(
                                                    'appointed_carriers',
                                                    newSelectedCarriers,
                                                    {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    }
                                                );
                                            }}
                                            disabled={loading}
                                        />
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </form>

                <TermsAndConditionsCheckbox
                    checked={watch('accept_terms') || false}
                    onChange={checked =>
                        setValue('accept_terms', checked, {
                            shouldValidate: true,
                        })
                    }
                    disabled={loading}
                    sx={{ mt: 1, mb: 2 }}
                />
                {errors.accept_terms && (
                    <Typography color="error" variant="caption">
                        {errors.accept_terms.message}
                    </Typography>
                )}
            </CardContent>

            <CardActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={!isValid || loading || !watch('accept_terms')}
                >
                    {loading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        'Create Account'
                    )}
                </Button>

                <OrDivider />

                <AuthButton component={Link} to="/login">
                    Back to Login
                </AuthButton>
            </CardActions>
        </AuthPageLayout>
    );
};

RegistrationPage.path = '/register'; // Define path for routing
